import * as React from "react";
import Slider, { SliderMarkLabel } from "@mui/material/Slider";
import { FrostDates, PlantingLocation } from "../Types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Tooltip, styled } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { FrostDatesContext, PlantingLocationContext } from "../AppContext";

enum SafetyLevel {
    Safest = 0,
    Safer = 1,
    Safe = 2,

}
const marks = [
    {
        value: SafetyLevel.Safest,
        label: `10%\u00A0Chance of\u00A0Frost`,
        lastProbability: 10,
        firstProbability: 10,
    },
    {
        value: SafetyLevel.Safer,
        label: '30%\u00A0Chance of\u00A0Frost',
        lastProbability: 30,
        firstProbability: 30,
    },
    {
        value: SafetyLevel.Safe,
        label: '50%\u00A0Chance of\u00A0Frost',
        lastProbability: 50,
        firstProbability: 50,
    },
];
function valuetext(value: number) {
    return marks[value].label;
}

function valueLabelFormat(value: number) {
    const mark = marks.findIndex((mark) => mark.value === value);
    return <Box>
        <Typography variant='h6' textAlign='center' textTransform='uppercase'>{marks[mark].firstProbability}% Chance</Typography>

    </Box>
}
const SliderBox = styled(Box)(({ theme }) => ({
    marginLeft: 'auto', marginRight: 'auto', width: '75%', display: 'block'
}));
const initialSafetyLevel = SafetyLevel.Safest;

function parseDate(date: string) {
    const [, month, day] = date.split('-');
    return dayjs().set('month', parseInt(month) - 1).set('date', parseInt(day));
}

function calcFrostDates(safetyLevel: SafetyLevel, plantingLocation: PlantingLocation) {
    const firstProbability = marks[safetyLevel].firstProbability;
    const lastProbability = marks[safetyLevel].lastProbability;
    const firstFrost = plantingLocation.closestStation.firstFrostData.find((frostData) => frostData.probability === firstProbability);
    const lastFrost = plantingLocation.closestStation.lastFrostData.find((frostData) => frostData.probability === lastProbability);
    if (firstFrost && lastFrost) {
        const firstDate = parseDate(firstFrost.date);
        const lastDate = parseDate(lastFrost.date);
        const seasonLength = lastDate.diff(firstDate, 'days');
        return { firstDate: { day: firstDate, probability: firstProbability }, lastDate: { day: lastDate, probability: lastProbability}, seasonLength };
    }
    throw new Error('Could not calculate frost dates');
}
export const BasicMark = (props: any) => {
    const index = props["data-index"] as number;
    const mark = props.ownerState.marks[index] as typeof marks[0];

    return (
        <SliderMarkLabel sx={{
            display: 'flex',
            flexDirection: 'column',
        }} {...props}>
            <Typography sx={{textWrap: 'nowrap', fontSize: '.9em'}}>{`${mark.firstProbability}% Chance`}</Typography>
            <Typography sx={{textWrap: 'nowrap', fontSize: '.9em'}}>of Frost</Typography>
        </SliderMarkLabel>
    );
};
export default function ProbabilitySlider() {
    const { setFrostDates } = useContext(FrostDatesContext);
    const { plantingLocation } = useContext(PlantingLocationContext);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const [newFrostDates, setNewFrostDates] = useState<FrostDates | null>(null);
    const [frostDatesChanged, setFrostDatesChanged] = useState(false);
    useEffect(() => {
        if (!initialDataLoaded && plantingLocation) {
            setFrostDates(calcFrostDates(initialSafetyLevel, plantingLocation));
            setInitialDataLoaded(true);
        } else if(frostDatesChanged) {
            setFrostDates(newFrostDates);
            setFrostDatesChanged(false);
        } else if(!plantingLocation) {
            setInitialDataLoaded(false);
        }
    }, [initialDataLoaded, plantingLocation, frostDatesChanged]);
    if (!plantingLocation) {
        return (
        <SliderBox>
            <Typography variant="h6" component="p">
                This tool will help you plan your seed planting schedule based on your local climate.
            </Typography>
        </SliderBox>)
    } else {
        return (
        <SliderBox>
            <Typography variant='h6' textAlign='left' textTransform='uppercase' sx={{textWrap: 'nowrap'}} ml={-2} letterSpacing={3} gutterBottom>
                Frost Date Probability&nbsp;
                <Tooltip title="The higher the safety level, the less likely your plants will be damaged by frost." placement="right">
                        <InfoIcon />
                </Tooltip>
            </Typography>
                <Slider
                    sx={{
                        color: 'white',
                        '& .MuiSlider-markLabel': {
                            color: 'white',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            width: 'min-content',
                            textWrap: 'wrap',
                        }
                    }}
                    defaultValue={initialSafetyLevel}
                    getAriaValueText={valuetext}
                    aria-labelledby="discrete-slider-custom"
                    step={1}
                    valueLabelDisplay="off"
                    marks={marks}
                    min={SafetyLevel.Safest}
                    max={SafetyLevel.Safe}
                    valueLabelFormat={valueLabelFormat}
                    slots={{
                        markLabel: BasicMark,
                    }}
                    onChange={(_, newValue: SafetyLevel | number[]) => {
                        if (typeof newValue === 'number') {

                            const firstProbability = marks[newValue].firstProbability;
                            const lastProbability = marks[newValue].lastProbability;
                            const firstFrost = plantingLocation.closestStation.firstFrostData.find((frostData) => frostData.probability === firstProbability);
                            const lastFrost = plantingLocation.closestStation.lastFrostData.find((frostData) => frostData.probability === lastProbability);
                            if (firstFrost && lastFrost) {
                                const firstDate = parseDate(firstFrost.date);
                                const lastDate = parseDate(lastFrost.date);
                                const seasonLength = lastDate.diff(firstDate, 'days');
                                setNewFrostDates({ firstDate: { day: firstDate, probability: firstProbability }, lastDate: { day: lastDate, probability: lastProbability }, seasonLength });
                                setFrostDatesChanged(true);
                            }
                        }
                    }}
                />
        </SliderBox>)
    }
}